import { TmBffHttpClient } from './tm-bff-http-client';

/**
 * HTTP client interface for @jkhy/web-ui-graph consumers.
 * Defines the contract expected by UsersAndRolesController.
 */
export interface GraphHttpClientContract {
    fetch<T>(config: {
        url: string;
        method?: string;
        body?: unknown;
        responseType?: 'json' | 'none';
    }): Promise<T>;
}

/**
 * Adapter that wraps TmBffHttpClient to implement the @jkhy/web-ui-graph
 * consumer contract: httpClient.fetch({ url, method, body })
 *
 * This adapter bridges the gap between JhHttpClient.request(...) and
 * the fetch(...) interface expected by UsersAndRolesController.
 */
export class TmBffGraphHttpClient implements GraphHttpClientContract {
    constructor(private readonly httpClient: TmBffHttpClient) {}

    public async fetch<T>(config: {
        url: string;
        method?: string;
        body?: unknown;
        responseType?: 'json' | 'none';
    }): Promise<T> {
        const { url, method = 'GET', body } = config;
        const resolvedUrl = this.resolveUrl(url);

        // Build the request config with proper typing for JhHttpClient
        const requestConfig: Record<string, unknown> = {};

        if (this.isAbsoluteUrl(url)) {
            requestConfig.withoutBase = true;
        }

        // Only add method if it's a valid HTTP verb
        if (method && method !== 'GET') {
            requestConfig.method = method;
        }

        // Only add body if it exists and is a plain object
        if (body && typeof body === 'object') {
            requestConfig.body = body;
        }

        // if the responseType is 'none', we don't want throw an error trying to parse the response as JSON, so we set the responseType to 'raw'
        if (config.responseType === 'none') {
            requestConfig.responseType = 'raw';
        }

        return this.httpClient.request<T>(resolvedUrl, requestConfig as never);
    }

    private resolveUrl(url: string): string {
        if (this.isAbsoluteUrl(url)) {
            return url;
        }

        return url.replace(/^\/+/, '');
    }

    private isAbsoluteUrl(url: string): boolean {
        return /^[a-z][a-z\d+\-.]*:\/\//i.test(url);
    }
}
